import { inject, Injectable } from '@angular/core';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { ToastController, ToastButton } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import {
  AnimationController,
  ModalController,
} from '@ionic/angular/standalone';

export interface SlideModalConfig {
  position?: 'right' | 'left';
  size?: 'small' | 'medium' | 'large' | 'full';
  showBackdrop?: boolean;
  backdropDismiss?: boolean;
}

export interface SlideModalAnimationConfig {
  duration?: number;
  easing?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SlideModalController {
  private modalController = inject(ModalController);
  private animationCtrl = inject(AnimationController);

  async create(options: {
    component: any;
    componentProps?: any;
    config?: SlideModalConfig;
    animationConfig?: SlideModalAnimationConfig;
  }) {
    const config = {
      position: 'right',
      size: 'medium',
      showBackdrop: true,
      backdropDismiss: true,
      ...options.config,
    };
    const animationConfig = {
      duration: 300,
      easing: 'ease-out',
      ...options.animationConfig,
    };

    const modal = await this.modalController.create({
      component: options.component,
      componentProps: options.componentProps,
      cssClass: this.getModalCssClass(config as any),
      showBackdrop: config.showBackdrop,
      backdropDismiss: config.backdropDismiss,
      enterAnimation: (baseEl: HTMLElement) =>
        this.createEnterAnimation(baseEl, config as any, animationConfig),
      leaveAnimation: (baseEl: HTMLElement) =>
        this.createLeaveAnimation(baseEl, config as any, animationConfig),
    });

    return modal;
  }

  private createEnterAnimation(
    baseEl: HTMLElement,
    config: SlideModalConfig,
    animationConfig: SlideModalAnimationConfig,
  ) {
    const root = baseEl.shadowRoot || baseEl;
    const backdrop = root.querySelector('ion-backdrop')!;
    const wrapper = root.querySelector('.modal-wrapper')!;

    const backdropAnimation = this.animationCtrl
      .create()
      .addElement(backdrop)
      .fromTo('opacity', '0.01', 'var(--backdrop-opacity)')
      .duration(animationConfig.duration!);

    const transforms = {
      right: { from: 'translateX(100%)', to: 'translateX(0%)' },
      left: { from: 'translateX(-100%)', to: 'translateX(0%)' },
      top: { from: 'translateY(-100%)', to: 'translateY(0%)' },
      bottom: { from: 'translateY(100%)', to: 'translateY(0%)' },
    };

    const transform = transforms[config.position!];

    const wrapperAnimation = this.animationCtrl
      .create()
      .addElement(wrapper)
      .beforeStyles({ transform: `${transform.from} !important` })
      .fromTo('transform', transform.from, transform.to)
      .duration(animationConfig.duration!)
      .easing(animationConfig.easing!);

    return this.animationCtrl
      .create()
      .addAnimation([backdropAnimation, wrapperAnimation]);
  }

  private createLeaveAnimation(
    baseEl: HTMLElement,
    config: SlideModalConfig,
    animationConfig: SlideModalAnimationConfig,
  ) {
    const root = baseEl.shadowRoot || baseEl;
    const backdrop = root.querySelector('ion-backdrop')!;
    const wrapper = root.querySelector('.modal-wrapper')!;

    const backdropAnimation = this.animationCtrl
      .create()
      .addElement(backdrop)
      .fromTo('opacity', 'var(--backdrop-opacity)', '0.01')
      .duration(animationConfig.duration!);

    const transforms = {
      right: { from: 'translateX(0%)', to: 'translateX(100%)' },
      left: { from: 'translateX(0%)', to: 'translateX(-100%)' },
      top: { from: 'translateY(0%)', to: 'translateY(-100%)' },
      bottom: { from: 'translateY(0%)', to: 'translateY(100%)' },
    };

    const transform = transforms[config.position!];

    const wrapperAnimation = this.animationCtrl
      .create()
      .addElement(wrapper)
      .fromTo('transform', transform.from, transform.to)
      .duration(animationConfig.duration!)
      .easing('ease-in');

    return this.animationCtrl
      .create()
      .addAnimation([backdropAnimation, wrapperAnimation]);
  }

  private getModalCssClass(config: SlideModalConfig): string {
    const classes = ['slide-modal'];
    if (config.position) classes.push(`slide-modal--${config.position}`);
    if (config.size) classes.push(`slide-modal--${config.size}`);
    return classes.join(' ');
  }
}
